import React from "react";

const DEFAULT_LABELS = ["1", "2", "3", "4", "5"];
const DEFAULT_VALUES = [5, 4, 5, 3, 5];

function polarToCartesian(cx, cy, radius, angleDeg) {
  const angleRad = ((angleDeg - 90) * Math.PI) / 180;
  return {
    x: cx + radius * Math.cos(angleRad),
    y: cy + radius * Math.sin(angleRad),
  };
}

function getPolygonPoints(cx, cy, radius, sides, rotation = 0) {
  return Array.from({ length: sides }, (_, i) => {
    const angle = rotation + (360 / sides) * i;
    return polarToCartesian(cx, cy, radius, angle);
  });
}

function pointsToString(points) {
  return points.map((p) => `${p.x},${p.y}`).join(" ");
}

export default function RadarChart({
  values = DEFAULT_VALUES,
  labels = DEFAULT_LABELS,
  maxValue = 5,
  levels = 5,
  size = 850,
  renderSize = 420,
  valueBadge = "96%",
  strokeColor = "#365561",
  fillColor = "#365561",
  background = "none",
}) {
  const safeValues = values.map((v) => Math.max(0, Math.min(v, maxValue)));
  const sides = safeValues.length;
  // const padding = size * 0.01;
  const padding = size * 0.07;
  const cx = size / 2;
  const cy = size / 1.90;
  const radius = size / 2 - padding;
  const rotation = 0;

  const gridPolygons = Array.from({ length: levels }, (_, i) => {
    const r = (radius * (i + 1)) / levels;
    return getPolygonPoints(cx, cy, r, sides, rotation);
  });

  const axisPoints = getPolygonPoints(cx, cy, radius, sides, rotation);

  const dataPoints = safeValues.map((value, i) => {
    const r = (radius * value) / maxValue;
    const angle = rotation + (360 / sides) * i;
    return polarToCartesian(cx, cy, r, angle);
  });

  const levelLabels = Array.from({ length: levels }, (_, i) => i + 1);
  // const fontSize = Math.max(10, Math.round(size * 0.03));
  const fontSize = Math.max(10, Math.round(size * 0.07));
  const badgeRadius = Math.max(24, size * 0.07);

  return (
    <div
      className="flex items-center justify-center"
      style={{ backgroundColor: background }}
    >
      <svg
        viewBox={`0 0 ${size} ${size - 45}`}
        width={renderSize}
        height={(renderSize * (size - 45)) / size}
        className="block shrink-0"
        role="img"
        aria-label="Radar chart"
      >
        <g>
          {gridPolygons.map((polygon, i) => (
            <polygon
              key={`grid-${i}`}
              points={pointsToString(polygon)}
              fill="none"
              stroke="#6b7280"
              strokeWidth={i === gridPolygons.length - 1 ? 4 : 3}
              opacity={0.7}
            />
          ))}

          {axisPoints.map((point, i) => (
            <line
              key={`axis-${i}`}
              x1={cx}
              y1={cy}
              x2={point.x}
              y2={point.y}
              stroke="#6b7280"
              strokeWidth="5"
              opacity="0.5"
            />
          ))}

          {/* {levelLabels
            .filter((level) => level < levels)
            .map((level, i) => {
              const y = cy - (radius * level) / levels;
              return (
                <text
                  key={`level-${level}`}
                  x={cx + 6}
                  y={y + 4}
                  fontSize={fontSize}
                  fill="#8b8b8b"
                  textAnchor="start"
                >
                  {level}
                </text>
              );
            })
          } */}

          {axisPoints.map((point, i) => {
            const dx = point.x - cx;
            const dy = point.y - cy;

            const distance = Math.sqrt(dx * dx + dy * dy) || 1;

            // Increase/decrease this value to control label distance
            const labelGap = size * 0.055;

            const labelX = point.x + (dx / distance) * labelGap;
            const labelY = point.y + (dy / distance) * labelGap;

            const anchor =
              Math.abs(dx) < 1 ? "middle" : dx > 0 ? "start" : "end";

            return (
              <text
                key={`label-${i}`}
                x={labelX}
                y={labelY}
                fontSize={fontSize + 2}
                fill="#6b7280"
                textAnchor={anchor}
                dominantBaseline="middle"
              >
                {labels[i]}
              </text>
            );
          })}

          <polygon
            points={pointsToString(dataPoints)}
            fill={fillColor}
            fillOpacity="0.80"
            stroke={strokeColor}
            strokeWidth="5"
          />

          <circle
            cx={cx}
            cy={cy}
            // r={badgeRadius}
            r={100}
            fill={strokeColor}
            stroke="white"
            strokeWidth="10"
          />
          <text
            x={cx}
            y={cy + 18}
            textAnchor="middle"
            fontSize={Math.max(12, Math.round(size * 0.07))}
            fontWeight="700"
            fill="white"
          >
            {valueBadge}
          </text>
        </g>
      </svg>
    </div>
  );
}